import React from "react";
import { useTranslation } from "react-i18next";
import {
  MdReceipt,
  MdShoppingCart,
  MdPerson,
  MdPalette,
  MdWidgets,
  MdGrid3X3,
  MdWallpaper,
  MdHome,
  MdDashboard,
  MdViewCarousel,
  MdViewCompact,
  MdLayers,
  MdWeb,
  MdVisibilityOff,
} from "react-icons/md";

export function LayoutPreviewCard({ layout, isActive, onClick }) {
  const { t } = useTranslation();

  const layoutDetails = {
    'fullscreen-banner': {
      name: t('LAYOUTS.FULLSCREEN_BANNER.NAME'),
      description: t('LAYOUTS.FULLSCREEN_BANNER.DESCRIPTION'),
      icon: <MdWallpaper className="text-2xl" />,
      color: 'from-blue-500 to-purple-600',
      preview: 'w-full h-24 rounded-lg bg-gradient-to-r from-blue-400 to-purple-500'
    },
    'split-screen': {
      name: t('LAYOUTS.SPLIT_SCREEN.NAME'),
      description: t('LAYOUTS.SPLIT_SCREEN.DESCRIPTION'),
      icon: <MdViewCompact className="text-2xl" />,
      color: 'from-green-500 to-emerald-600',
      preview: 'grid grid-cols-2 gap-2 h-24'
    },
    'minimal': {
      name: t('LAYOUTS.MINIMAL.NAME'),
      description: t('LAYOUTS.MINIMAL.DESCRIPTION'),
      icon: <MdWeb className="text-2xl" />,
      color: 'from-amber-500 to-orange-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500'
    },
    'carousel': {
      name: t('LAYOUTS.CAROUSEL.NAME'),
      description: t('LAYOUTS.CAROUSEL.DESCRIPTION'),
      icon: <MdViewCarousel className="text-2xl" />,
      color: 'from-pink-500 to-rose-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-pink-400 to-rose-500'
    },
    'gradient': {
      name: t('LAYOUTS.GRADIENT.NAME'),
      description: t('LAYOUTS.GRADIENT.DESCRIPTION'),
      icon: <MdLayers className="text-2xl" />,
      color: 'from-purple-500 to-indigo-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-purple-400 to-indigo-500'
    },
    'centered': {
      name: t('LAYOUTS.CENTERED.NAME'),
      description: t('LAYOUTS.CENTERED.DESCRIPTION'),
      icon: <MdDashboard className="text-2xl" />,
      color: 'from-purple-500 to-pink-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-purple-400 to-pink-500'
    },
    'glass-morphism': {
      name: t('LAYOUTS.GLASS_MORPHISM.NAME'),
      description: t('LAYOUTS.GLASS_MORPHISM.DESCRIPTION'),
      icon: <MdPalette className="text-2xl" />,
      color: 'from-cyan-500 to-blue-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-cyan-400 to-blue-500 backdrop-blur-sm'
    },
    'parallax': {
      name: t('LAYOUTS.PARALLAX.NAME'),
      description: t('LAYOUTS.PARALLAX.DESCRIPTION'),
      icon: <MdLayers className="text-2xl" />,
      color: 'from-pink-500 to-purple-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-pink-400 to-purple-500'
    },
    'product-showcase': {
      name: t('LAYOUTS.PRODUCT_SHOWCASE.NAME'),
      description: t('LAYOUTS.PRODUCT_SHOWCASE.DESCRIPTION'),
      icon: <MdShoppingCart className="text-2xl" />,
      color: 'from-orange-500 to-red-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-orange-400 to-red-500'
    },
    'dark-minimal': {
      name: t('LAYOUTS.DARK_MINIMAL.NAME'),
      description: t('LAYOUTS.DARK_MINIMAL.DESCRIPTION'),
      icon: <MdVisibilityOff className="text-2xl" />,
      color: 'from-gray-800 to-gray-900',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-gray-700 to-gray-900'
    },
    'animated': {
      name: t('LAYOUTS.ANIMATED.NAME'),
      description: t('LAYOUTS.ANIMATED.DESCRIPTION'),
      icon: <MdWidgets className="text-2xl" />,
      color: 'from-blue-500 to-indigo-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-blue-400 to-indigo-500 animate-pulse'
    },
    'sidebar': {
      name: t('LAYOUTS.SIDEBAR.NAME'),
      description: t('LAYOUTS.SIDEBAR.DESCRIPTION'),
      icon: <MdViewCompact className="text-2xl" />,
      color: 'from-teal-500 to-green-600',
      preview: 'grid grid-cols-3 gap-1 h-24'
    },
    'card-grid': {
      name: t('LAYOUTS.CARD_GRID.NAME'),
      description: t('LAYOUTS.CARD_GRID.DESCRIPTION'),
      icon: <MdGrid3X3 className="text-2xl" />,
      color: 'from-indigo-500 to-purple-600',
      preview: 'grid grid-cols-2 gap-1 h-24'
    },
    'masonry': {
      name: t('LAYOUTS.MASONRY.NAME'),
      description: t('LAYOUTS.MASONRY.DESCRIPTION'),
      icon: <MdWallpaper className="text-2xl" />,
      color: 'from-lime-500 to-green-600',
      preview: 'grid grid-cols-3 gap-1 h-24'
    },
    'hero-sidebar': {
      name: t('LAYOUTS.HERO_SIDEBAR.NAME'),
      description: t('LAYOUTS.HERO_SIDEBAR.DESCRIPTION'),
      icon: <MdHome className="text-2xl" />,
      color: 'from-violet-500 to-purple-600',
      preview: 'grid grid-cols-4 gap-1 h-24'
    },
    'split-content': {
      name: t('LAYOUTS.SPLIT_CONTENT.NAME'),
      description: t('LAYOUTS.SPLIT_CONTENT.DESCRIPTION'),
      icon: <MdWeb className="text-2xl" />,
      color: 'from-teal-500 to-cyan-600',
      preview: 'grid grid-cols-2 gap-1 h-24'
    },
    'floating-elements': {
      name: t('LAYOUTS.FLOATING_ELEMENTS.NAME'),
      description: t('LAYOUTS.FLOATING_ELEMENTS.DESCRIPTION'),
      icon: <MdWidgets className="text-2xl" />,
      color: 'from-rose-500 to-pink-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-rose-400 to-pink-500'
    },
    'timeline': {
      name: t('LAYOUTS.TIMELINE.NAME'),
      description: t('LAYOUTS.TIMELINE.DESCRIPTION'),
      icon: <MdReceipt className="text-2xl" />,
      color: 'from-amber-500 to-orange-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500'
    },
    'testimonial': {
      name: t('LAYOUTS.TESTIMONIAL.NAME'),
      description: t('LAYOUTS.TESTIMONIAL.DESCRIPTION'),
      icon: <MdPerson className="text-2xl" />,
      color: 'from-emerald-500 to-green-600',
      preview: 'h-24 rounded-lg bg-gradient-to-r from-emerald-400 to-green-500'
    }
  };

  const details = layoutDetails[layout] || layoutDetails['fullscreen-banner'];

  return (
    <div
      onClick={onClick}
      className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] dark:hover:shadow-gray-800 ${
        isActive
          ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30 ring-2 ring-blue-200 dark:ring-blue-800'
          : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg bg-gradient-to-r ${details.color} text-white`}>
          {details.icon}
        </div>
        {isActive && (
          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
            {t('COMMONONE.ACTIVE')}
          </span>
        )}
      </div>

      <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
        {details.name}
      </h4>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {details.description}
      </p>

      <div className="mt-4">
        <div className={`${details.preview} dark:opacity-90`}>
          {layout === 'split-screen' && (
            <>
              <div className="bg-gradient-to-r from-blue-300 to-blue-400 dark:from-blue-600 dark:to-blue-700 rounded"></div>
              <div className="bg-gray-100 dark:bg-gray-700 rounded"></div>
            </>
          )}
          {layout === 'card-grid' && (
            <>
              <div className="bg-gradient-to-r from-indigo-300 to-indigo-400 dark:from-indigo-600 dark:to-indigo-700 rounded"></div>
              <div className="bg-gradient-to-r from-purple-300 to-purple-400 dark:from-purple-600 dark:to-purple-700 rounded"></div>
              <div className="bg-gradient-to-r from-indigo-300 to-indigo-400 dark:from-indigo-600 dark:to-indigo-700 rounded"></div>
              <div className="bg-gradient-to-r from-purple-300 to-purple-400 dark:from-purple-600 dark:to-purple-700 rounded"></div>
            </>
          )}
          {layout === 'masonry' && (
            <>
              <div className="bg-gradient-to-r from-lime-300 to-lime-400 dark:from-lime-600 dark:to-lime-700 rounded row-span-2"></div>
              <div className="bg-gradient-to-r from-green-300 to-green-400 dark:from-green-600 dark:to-green-700 rounded"></div>
              <div className="bg-gradient-to-r from-lime-300 to-lime-400 dark:from-lime-600 dark:to-lime-700 rounded"></div>
            </>
          )}
          {layout === 'sidebar' && (
            <>
              <div className="bg-gradient-to-r from-teal-300 to-teal-400 dark:from-teal-600 dark:to-teal-700 rounded col-span-1"></div>
              <div className="bg-gradient-to-r from-green-300 to-green-400 dark:from-green-600 dark:to-green-700 rounded col-span-2"></div>
            </>
          )}
          {layout === 'hero-sidebar' && (
            <>
              <div className="bg-gradient-to-r from-violet-300 to-violet-400 dark:from-violet-600 dark:to-violet-700 rounded col-span-3"></div>
              <div className="bg-gradient-to-r from-purple-300 to-purple-400 dark:from-purple-600 dark:to-purple-700 rounded"></div>
            </>
          )}
          {layout === 'split-content' && (
            <>
              <div className="bg-gradient-to-r from-teal-300 to-teal-400 dark:from-teal-600 dark:to-teal-700 rounded"></div>
              <div className="bg-gradient-to-r from-cyan-300 to-cyan-400 dark:from-cyan-600 dark:to-cyan-700 rounded"></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}